<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { uploadFile } from '@/api/file'
import { compressImage } from '@/utils/imageCompress'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['update:show', 'confirm'])

const form = reactive({
  contentType: 1,
  content: '',
  images: '',
  videoUrl: '',
  videoCover: '',
  publisherName: '',
  publisherAvatar: '',
  linkUrl: '',
  linkTitle: '',
  isTop: 0,
  topSort: 0,
  topExpireTime: ''
})

const submitting = ref(false)
const imageInput = ref()
const videoInput = ref()
const coverInput = ref()
const avatarInput = ref()

// 本地待上传文件
const pendingImages = ref([])
const pendingVideoFile = ref(null)
const pendingCoverFile = ref(null)
const pendingAvatarFile = ref(null)
const avatarPreview = ref('')

const isImageText = computed(() => form.contentType === 1)
const isVideo = computed(() => form.contentType === 2)

watch(() => props.show, (val) => {
  if (val) {
    resetForm()
  }
})

const resetForm = () => {
  Object.assign(form, {
    contentType: 1,
    content: '',
    images: '',
    videoUrl: '',
    videoCover: '',
    publisherName: '',
    publisherAvatar: '',
    linkUrl: '',
    linkTitle: '',
    isTop: 0,
    topSort: 0,
    topExpireTime: ''
  })
  pendingImages.value = []
  pendingVideoFile.value = null
  pendingCoverFile.value = null
  pendingAvatarFile.value = null
  avatarPreview.value = ''
}

// ===== 图片处理 =====
const triggerImageUpload = () => imageInput.value?.click()

const handleImageChange = async (event) => {
  const files = Array.from(event.target.files)
  if (!files.length) return

  for (const file of files) {
    if (!file.type.startsWith('image/')) {
      ElMessage.warning('请选择图片文件')
      continue
    }
    if (file.size > 10 * 1024 * 1024) {
      ElMessage.warning('图片大小不能超过10MB')
      continue
    }
    if (pendingImages.value.length >= 9) {
      ElMessage.warning('最多上传9张图片')
      break
    }
    const compressed = await compressImage(file)
    pendingImages.value.push({
      file: compressed,
      preview: URL.createObjectURL(compressed)
    })
  }
  event.target.value = ''
}

const removeImage = (index) => {
  URL.revokeObjectURL(pendingImages.value[index].preview)
  pendingImages.value.splice(index, 1)
}

// ===== 视频处理 =====
const triggerVideoUpload = () => videoInput.value?.click()

const handleVideoChange = (event) => {
  const file = event.target.files[0]
  if (!file) return
  if (!file.type.startsWith('video/')) {
    ElMessage.warning('请选择视频文件')
    return
  }
  if (file.size > 100 * 1024 * 1024) {
    ElMessage.warning('视频大小不能超过100MB')
    return
  }
  pendingVideoFile.value = {
    file,
    preview: URL.createObjectURL(file)
  }
  event.target.value = ''
}

const removeVideo = () => {
  if (pendingVideoFile.value?.preview) {
    URL.revokeObjectURL(pendingVideoFile.value.preview)
  }
  pendingVideoFile.value = null
}

// ===== 视频封面处理 =====
const triggerCoverUpload = () => coverInput.value?.click()

const handleCoverChange = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  if (!file.type.startsWith('image/')) {
    ElMessage.warning('请选择图片文件')
    return
  }
  const compressed = await compressImage(file)
  pendingCoverFile.value = {
    file: compressed,
    preview: URL.createObjectURL(compressed)
  }
  event.target.value = ''
}

const removeCover = () => {
  if (pendingCoverFile.value?.preview) {
    URL.revokeObjectURL(pendingCoverFile.value.preview)
  }
  pendingCoverFile.value = null
}

// ===== 头像处理 =====
const triggerAvatarUpload = () => avatarInput.value?.click()

const handleAvatarChange = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  if (!file.type.startsWith('image/')) {
    ElMessage.warning('请选择图片文件')
    return
  }
  const compressed = await compressImage(file)
  pendingAvatarFile.value = compressed
  avatarPreview.value = URL.createObjectURL(compressed)
  event.target.value = ''
}

const handleClose = () => {
  emit('update:show', false)
}

const handleSubmit = async () => {
  // 校验
  if (isImageText.value && pendingImages.value.length === 0 && !form.content.trim()) {
    return ElMessage.warning('请输入动态内容或上传图片')
  }
  if (isVideo.value && !pendingVideoFile.value) {
    return ElMessage.warning('请上传视频')
  }

  submitting.value = true
  try {
    // 上传图片
    let images = ''
    if (isImageText.value && pendingImages.value.length > 0) {
      const urls = []
      for (const img of pendingImages.value) {
        const result = await uploadFile(img.file, 'circle', 'public')
        urls.push(result.url || result)
      }
      images = urls.join(',')
    }

    // 上传视频
    let videoUrl = ''
    if (isVideo.value && pendingVideoFile.value) {
      const result = await uploadFile(pendingVideoFile.value.file, 'circle', 'public')
      videoUrl = result.url || result
    }

    // 上传视频封面
    let videoCover = ''
    if (isVideo.value && pendingCoverFile.value) {
      const result = await uploadFile(pendingCoverFile.value.file, 'circle', 'public')
      videoCover = result.url || result
    }

    // 上传头像
    let publisherAvatar = ''
    if (pendingAvatarFile.value) {
      const result = await uploadFile(pendingAvatarFile.value, 'circle', 'public')
      publisherAvatar = result.url || result
    }

    const data = {
      contentType: form.contentType,
      content: form.content || undefined,
      images: images || undefined,
      videoUrl: videoUrl || undefined,
      videoCover: videoCover || undefined,
      publisherName: form.publisherName || undefined,
      publisherAvatar: publisherAvatar || undefined,
      linkUrl: form.linkUrl || undefined,
      linkTitle: form.linkTitle || undefined,
      isTop: form.isTop,
      topSort: form.isTop === 1 ? form.topSort : undefined,
      topExpireTime: form.isTop === 1 && form.topExpireTime ? form.topExpireTime : undefined
    }

    emit('confirm', data)
  } catch (error) {
    ElMessage.error('文件上传失败')
    submitting.value = false
  }
}

const done = () => {
  submitting.value = false
  handleClose()
}

defineExpose({ done })
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="handleClose"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-3xl w-full max-h-[90vh] flex flex-col">
            <!-- 头部 -->
            <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
              <h3 class="text-lg font-semibold text-slate-900">发布动态</h3>
              <button
                @click="handleClose"
                class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 内容区 -->
            <div class="flex-1 overflow-y-auto p-6">
              <div class="space-y-6">
                <!-- 内容类型选择 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    内容类型 <span class="text-red-500">*</span>
                  </label>
                  <div class="flex items-center gap-3">
                    <button
                      @click="form.contentType = 1"
                      :class="[
                        'px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border',
                        form.contentType === 1
                          ? 'bg-primary-50 text-primary-700 border-primary-300'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      ]"
                    >
                      <span class="flex items-center gap-1.5">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                        图文
                      </span>
                    </button>
                    <button
                      @click="form.contentType = 2"
                      :class="[
                        'px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border',
                        form.contentType === 2
                          ? 'bg-primary-50 text-primary-700 border-primary-300'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      ]"
                    >
                      <span class="flex items-center gap-1.5">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                        </svg>
                        短视频
                      </span>
                    </button>
                  </div>
                </div>

                <!-- 动态内容 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-1">动态内容</label>
                  <textarea
                    v-model="form.content"
                    rows="4"
                    placeholder="请输入动态文字内容"
                    class="form-textarea"
                  ></textarea>
                </div>

                <!-- 图片上传（图文模式） -->
                <div v-if="isImageText">
                  <label class="block text-sm font-medium text-slate-700 mb-2">图片（最多9张）</label>
                  <div class="grid grid-cols-4 gap-2">
                    <!-- 已选图片 -->
                    <div
                      v-for="(img, index) in pendingImages"
                      :key="index"
                      class="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group"
                    >
                      <img :src="img.preview" class="w-full h-full object-cover" />
                      <button
                        @click="removeImage(index)"
                        class="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/50 text-white hover:bg-red-500 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                    <!-- 添加按钮 -->
                    <div
                      v-if="pendingImages.length < 9"
                      @click="triggerImageUpload"
                      class="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center hover:border-primary-400 hover:bg-primary-50/30 transition-colors cursor-pointer"
                    >
                      <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                      </svg>
                      <span class="text-xs text-slate-400 mt-1">添加图片</span>
                    </div>
                  </div>
                  <input
                    ref="imageInput"
                    type="file"
                    accept="image/*"
                    multiple
                    class="hidden"
                    @change="handleImageChange"
                  />
                </div>

                <!-- 视频上传（短视频模式） -->
                <div v-if="isVideo">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    视频 <span class="text-red-500">*</span>
                  </label>
                  <div v-if="pendingVideoFile" class="relative rounded-lg overflow-hidden border border-slate-200">
                    <video :src="pendingVideoFile.preview" class="w-full max-h-48 object-contain bg-black" controls></video>
                    <button
                      @click="removeVideo"
                      class="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-white hover:bg-red-500 transition-colors cursor-pointer"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                  <div
                    v-else
                    @click="triggerVideoUpload"
                    class="w-full h-36 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center hover:border-primary-400 hover:bg-primary-50/30 transition-colors cursor-pointer"
                  >
                    <svg class="w-8 h-8 text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                    <span class="text-sm text-slate-500">点击上传视频</span>
                    <span class="text-xs text-slate-400 mt-1">支持MP4格式，不超过100MB</span>
                  </div>
                  <input ref="videoInput" type="file" accept="video/*" class="hidden" @change="handleVideoChange" />

                  <!-- 视频封面 -->
                  <label class="block text-sm font-medium text-slate-700 mb-2 mt-4">视频封面</label>
                  <div v-if="pendingCoverFile" class="relative inline-block">
                    <img :src="pendingCoverFile.preview" class="h-24 rounded-lg border border-slate-200 object-cover" />
                    <button
                      @click="removeCover"
                      class="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                  <div
                    v-else
                    @click="triggerCoverUpload"
                    class="w-32 h-24 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center hover:border-primary-400 hover:bg-primary-50/30 transition-colors cursor-pointer"
                  >
                    <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    <span class="text-xs text-slate-400 mt-1">上传封面</span>
                  </div>
                  <input ref="coverInput" type="file" accept="image/*" class="hidden" @change="handleCoverChange" />
                </div>

                <!-- 发布者信息 -->
                <div>
                  <h4 class="text-sm font-semibold text-slate-900 mb-3">发布者信息</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-slate-700 mb-1">展示名称</label>
                      <input
                        v-model="form.publisherName"
                        type="text"
                        placeholder="如：官方运营（不填使用账号名称）"
                        class="form-input w-full"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-slate-700 mb-1">展示头像</label>
                      <div class="flex items-center gap-3">
                        <div
                          @click="triggerAvatarUpload"
                          class="w-9 h-9 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center hover:border-primary-400 transition-colors cursor-pointer overflow-hidden shrink-0"
                        >
                          <img v-if="avatarPreview" :src="avatarPreview" class="w-full h-full object-cover" />
                          <svg v-else class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                          </svg>
                        </div>
                        <span class="text-xs text-slate-400">不填使用默认头像</span>
                      </div>
                      <input ref="avatarInput" type="file" accept="image/*" class="hidden" @change="handleAvatarChange" />
                    </div>
                  </div>
                </div>

                <!-- 跳转链接 -->
                <div>
                  <h4 class="text-sm font-semibold text-slate-900 mb-3">跳转链接</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <div class="col-span-2">
                      <label class="block text-sm font-medium text-slate-700 mb-1">链接URL</label>
                      <input
                        v-model="form.linkUrl"
                        type="text"
                        placeholder="点击动态时跳转的链接地址"
                        class="form-input w-full"
                      />
                    </div>
                    <div class="col-span-2">
                      <label class="block text-sm font-medium text-slate-700 mb-1">链接标题</label>
                      <input
                        v-model="form.linkTitle"
                        type="text"
                        placeholder="如：点击查看详情"
                        class="form-input w-full"
                      />
                    </div>
                  </div>
                </div>

                <!-- 置顶设置 -->
                <div>
                  <h4 class="text-sm font-semibold text-slate-900 mb-3">置顶设置</h4>
                  <div class="space-y-3">
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        :checked="form.isTop === 1"
                        @change="form.isTop = $event.target.checked ? 1 : 0"
                        class="w-4 h-4 text-primary-600 rounded border-slate-300 focus:ring-primary-500 cursor-pointer"
                      />
                      <span class="text-sm text-slate-700">发布后置顶</span>
                    </label>
                    <template v-if="form.isTop === 1">
                      <div class="grid grid-cols-2 gap-4 ml-6">
                        <div>
                          <label class="block text-sm font-medium text-slate-700 mb-1">排序值</label>
                          <input
                            v-model.number="form.topSort"
                            type="number"
                            min="0"
                            placeholder="值越小越靠前"
                            class="form-input w-full"
                          />
                        </div>
                        <div>
                          <label class="block text-sm font-medium text-slate-700 mb-1">过期时间</label>
                          <input
                            v-model="form.topExpireTime"
                            type="datetime-local"
                            class="form-input w-full"
                          />
                          <p class="text-xs text-slate-400 mt-1">不填则永不过期</p>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- 底部按钮 -->
            <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 shrink-0">
              <button
                @click="handleClose"
                class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                @click="handleSubmit"
                :disabled="submitting"
                class="btn-primary"
              >
                {{ submitting ? '发布中...' : '发布' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
